import type { DocState, InsuranceReminderDraft, InsuranceRow } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { errorMessage, get, post } from "../api";
import { useSession } from "../session";
import { Alert, Badge, Button, Dialog, Empty, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, cx, formatDate } from "./ui";

type Data = { rows: InsuranceRow[]; emailOn: boolean; remindersOn: boolean; reminderDays: number };

const STATE: Record<DocState, { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  ok: { label: "OK", tone: "ok" },
  expiring: { label: "Expiring", tone: "wait" },
  expired: { label: "Expired", tone: "brand" },
  missing: { label: "Missing", tone: "brand" },
  waived: { label: "Waived", tone: "off" },
};

/** "Expired Oct 1, 2026" / "Expires Nov 1" / "Missing". */
function Doc({ label, state, expiresOn }: { label: string; state: DocState; expiresOn: string | null }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="text-muted">{label}</span>
      <Badge tone={STATE[state].tone}>{STATE[state].label}</Badge>
      {expiresOn && state !== "waived" && <span className={cx(state === "expired" && "font-semibold text-danger")}>{formatDate(expiresOn)}</span>}
    </span>
  );
}

const STAGE = { soon: "before it expired", expired: "after it expired", manual: "by hand" };

/** Subs' insurance: the dashboard card (needing attention) or the full report. */
export function InsuranceList({ all, limit }: { all?: boolean; limit?: number }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["insurance", all ? "all" : "attention"], queryFn: () => get<Data>(`/api/insurance${all ? "?all=1" : ""}`) });
  const [shown, setShown] = useState<"attention" | "all">("attention");
  const [applying, setApplying] = useState<InsuranceRow | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["insurance"] });
  const [reminding, setReminding] = useState<InsuranceRow | null>(null);
  const dismiss = useMutation({ mutationFn: (id: string) => post(`/api/certificate-checks/${id}/dismiss`), onSuccess: refresh });
  const edit = can("customers.edit");
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const d = q.data;
  let rows = all && shown === "attention" ? d.rows.filter((r) => r.attention && !r.inactive) : d.rows;
  const more = limit && rows.length > limit ? rows.length - limit : 0;
  if (limit) rows = rows.slice(0, limit);
  return (
    <div className="flex flex-col">
      {all && (
        <div className="flex flex-wrap items-center gap-2 px-5 pt-3">
          {(["attention", "all"] as const).map((v) => (
            <button key={v} onClick={() => setShown(v)} className={cx("rounded-full px-3 py-1 text-sm font-semibold", shown === v ? "bg-ink text-white" : "bg-mist text-ink-soft hover:text-ink")}>
              {v === "attention" ? "Needs attention" : "All subs"}
            </button>
          ))}
          <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(toCsv(d.rows))}`} download="sub-insurance.csv" className="ml-auto text-sm font-semibold text-brand hover:underline">
            Download CSV
          </a>
        </div>
      )}
      {!d.emailOn && <p className="px-5 pt-3 text-xs text-muted">Reminder emails start once sending email from the CRM is on.</p>}
      {d.emailOn && !d.remindersOn && <p className="px-5 pt-3 text-xs text-muted">Automatic reminder emails are off (Settings → Email and text templates).</p>}
      {(said || dismiss.error) && (
        <div className="px-5 pt-3">
          {said && <Alert tone="ok">{said}</Alert>}
          {dismiss.error && <Alert>{errorMessage(dismiss.error)}</Alert>}
        </div>
      )}
      {!rows.length ? (
        <Empty>{all && shown === "all" ? "No subcontractors yet." : "Everyone's insurance is current."}</Empty>
      ) : (
        <ul>
          {rows.map((r) => (
            <li key={r.contactId} className="flex flex-col gap-1.5 border-b border-mist px-5 py-3 text-sm last:border-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Link to={`/contacts/${r.contactId}`} className="font-semibold hover:text-brand">
                  {r.name}
                </Link>
                {r.inactive && <Badge tone="off">Inactive</Badge>}
                {r.waived ? (
                  <Badge tone="off">Insurance waived</Badge>
                ) : (
                  <>
                    <Doc label={r.workersComp.kind === "exemption" ? "WC exemption" : "Workers' comp"} state={r.workersComp.state} expiresOn={r.workersComp.expiresOn} />
                    <Doc label="Liability" state={r.generalLiability.state} expiresOn={r.generalLiability.expiresOn} />
                  </>
                )}
                {r.w9 === "missing" && <Doc label="W-9" state="missing" expiresOn={null} />}
                <span className="flex-1" />
                {edit && !r.waived && ([r.workersComp.state, r.generalLiability.state].some((s) => s !== "ok" && s !== "waived")) && (
                  <Button className="px-3 py-1" onClick={() => (setSaid(null), setReminding(r))}>
                    Remind…
                  </Button>
                )}
              </div>
              <div className="text-xs text-muted">
                {r.lastReminder
                  ? r.lastReminder.error
                    ? `Reminder to ${r.lastReminder.to} didn't send (${formatDate(r.lastReminder.at)}): ${r.lastReminder.error}`
                    : `Reminded ${formatDate(r.lastReminder.at)} (${STAGE[r.lastReminder.stage]}) at ${r.lastReminder.to}`
                  : !r.email && !r.waived
                    ? "No email address on file: add one to send reminders."
                    : null}
                {all && r.lastWorkOrderAt && <span>{r.lastReminder || !r.email ? " · " : ""}Last work order {formatDate(r.lastWorkOrderAt)}</span>}
              </div>
              {r.check && (
                <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] bg-mist/70 px-3 py-2">
                  <span className="font-semibold">New certificate</span>
                  {r.check.fileUrl && (
                    <a href={r.check.fileUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                      {r.check.fileName}
                    </a>
                  )}
                  <span className="text-muted">
                    {r.check.status === "reading"
                      ? "Reading it…"
                      : r.check.status === "failed"
                        ? r.check.note || "Couldn't read it; enter the dates."
                        : [r.check.wcExpiresOn && `${r.check.wcKind === "exemption" ? "WC exemption" : "Workers' comp"} through ${formatDate(r.check.wcExpiresOn)}`, r.check.glExpiresOn && `liability through ${formatDate(r.check.glExpiresOn)}`, r.check.note].filter(Boolean).join(" · ") || "No dates found."}
                  </span>
                  <span className="flex-1" />
                  {edit && r.check.status !== "reading" && (
                    <>
                      <Button variant="primary" className="px-3 py-1" onClick={() => setApplying(r)}>
                        Check and file it
                      </Button>
                      <Button variant="quiet" className="px-3 py-1" busy={dismiss.isPending} onClick={() => window.confirm("Not a certificate to file? It stays in their files.") && dismiss.mutate(r.check!.id)}>
                        Dismiss
                      </Button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {more > 0 && (
        <Link to="/reports#insurance" className="border-t border-mist px-5 py-2 text-sm font-semibold text-brand hover:underline">
          {more} more
        </Link>
      )}
      {reminding && <RemindDialog contactId={reminding.contactId} name={reminding.name} onClose={() => setReminding(null)} onSent={async (msg) => (setReminding(null), setSaid(msg), await refresh())} />}
      {applying?.check && <ApplyDialog row={applying} onClose={() => setApplying(null)} onDone={async () => (setApplying(null), await refresh())} />}
    </div>
  );
}

/** The dates as read, to check against the certificate and correct, then file on the sub's paperwork. */
function ApplyDialog({ row, onClose, onDone }: { row: InsuranceRow; onClose: () => void; onDone: () => void }) {
  const k = row.check!;
  const [wcKind, setWcKind] = useState<"policy" | "exemption">(k.wcKind ?? row.workersComp.kind ?? "policy");
  const [wc, setWc] = useState(k.wcExpiresOn ?? "");
  const [gl, setGl] = useState(k.glExpiresOn ?? "");
  const apply = useMutation({ mutationFn: () => post(`/api/certificate-checks/${k.id}/apply`, { wcKind: wc ? wcKind : null, wcExpiresOn: wc || null, glExpiresOn: gl || null }), onSuccess: onDone });
  return (
    <Dialog
      title={`${row.name}: new certificate`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={apply.isPending} disabled={!wc && !gl} onClick={() => apply.mutate()}>
            File it
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {apply.error && <Alert>{errorMessage(apply.error)}</Alert>}
        <p className="text-ink-soft">
          Check these against{" "}
          {k.fileUrl ? (
            <a href={k.fileUrl} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
              the certificate
            </a>
          ) : (
            "the certificate"
          )}
          . Filing it puts it on their paperwork with these dates; leave a date blank if it isn't on this certificate.
        </p>
        {k.note && <Alert tone="info">{k.note}</Alert>}
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="Workers' comp" value={wcKind} onChange={(e) => setWcKind(e.target.value as "policy" | "exemption")}>
            <option value="policy">Policy</option>
            <option value="exemption">Oklahoma exemption</option>
          </SelectField>
          <Field label="Workers' comp expires" type="date" value={wc} onChange={(e) => setWc(e.target.value)} />
          <span className="hidden sm:block" />
          <Field label="General liability expires" type="date" value={gl} onChange={(e) => setGl(e.target.value)} />
        </div>
      </div>
    </Dialog>
  );
}

function toCsv(rows: InsuranceRow[]) {
  const cell = (v: string | null | undefined) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Sub", "Email", "Phone", "Inactive", "Waived", "Workers' comp", "WC expires", "Liability", "GL expires", "W-9", "Last reminder", "Last work order"];
  return [head.map(cell).join(","), ...rows.map((r) => [r.name, r.email, r.phone, r.inactive ? "yes" : "", r.waived ? "yes" : "", r.workersComp.state, r.workersComp.expiresOn, r.generalLiability.state, r.generalLiability.expiresOn, r.w9, r.lastReminder?.at.slice(0, 10), r.lastWorkOrderAt?.slice(0, 10)].map(cell).join(","))].join("\n");
}

/** The dashboard card: subs whose insurance needs something (shown only to people who deal with subs). */
export function InsuranceCard() {
  const { can } = useSession();
  if (!can("workorders.edit", "customers.edit")) return null;
  return (
    <Panel>
      <PanelHeader title="Sub insurance to chase" description="Expired or expiring workers' comp and liability, and certificates subs sent in." action={<Link to="/reports#insurance" className="text-sm font-semibold text-brand hover:underline">Report</Link>} />
      <InsuranceList limit={8} />
    </Panel>
  );
}

/** Remind a sub by email or text: the wording from the templates (what's lapsed and when), editable, with their upload link. */
export function RemindDialog({ contactId, name, onClose, onSent }: { contactId: string; name: string; onClose: () => void; onSent: (said: string) => void }) {
  const { can } = useSession();
  const draft = useQuery({ queryKey: ["insurance-draft", contactId], queryFn: () => post<InsuranceReminderDraft>(`/api/insurance/${contactId}/draft`), retry: false, staleTime: Infinity });
  const [how, setHow] = useState<"email" | "text">("email");
  const [to, setTo] = useState<string | null>(null);
  const [subject, setSubject] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const d = draft.data;
  const v = {
    to: to ?? (how === "email" ? d?.email.to : d?.text.to) ?? "",
    subject: subject ?? d?.email.subject ?? "",
    message: message ?? (how === "email" ? d?.email.message : d?.text.message) ?? "",
  };
  const ready = how === "email" ? d?.emailOn : d?.textOn && can("comms.send");
  const send = useMutation({
    mutationFn: () => post<{ to: string; redirectedTo: string | null }>(`/api/insurance/${contactId}/remind`, { how, to: v.to, subject: v.subject, message: v.message }),
    onSuccess: (x) => onSent(`${how === "email" ? "Emailed" : "Texted"} ${name} at ${x.to}${x.redirectedTo ? ` (staging: it went to ${x.redirectedTo})` : ""}, with their upload link.`),
  });
  const pick = (h: "email" | "text") => (setHow(h), setTo(null), setMessage(null));
  return (
    <Dialog
      title={`Remind ${name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={send.isPending} disabled={!d || !ready || !v.to.trim() || !v.message.trim()} onClick={() => send.mutate()}>
            {how === "email" ? "Send email" : "Send text"}
          </Button>
        </>
      }
    >
      {draft.isLoading ? (
        <Loading />
      ) : draft.error || !d ? (
        <Alert>{errorMessage(draft.error)}</Alert>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm font-semibold">{d.about}.</p>
          <div role="radiogroup" className="inline-flex w-fit rounded-[var(--radius-control)] bg-mist p-0.5">
            {(["email", "text"] as const).map((h) => (
              <button key={h} type="button" role="radio" aria-checked={how === h} onClick={() => pick(h)} className={cx("rounded-[6px] px-3 py-1.5 text-sm font-semibold", how === h ? "bg-white shadow-sm" : "text-muted hover:text-ink")}>
                {h === "email" ? "Email" : "Text"}
              </button>
            ))}
          </div>
          {!ready && <Alert tone="info">{how === "email" ? "Sending email from the CRM isn't on yet (Amazon is approving it)." : "Texting isn't available for you here."}</Alert>}
          {send.error && <Alert>{errorMessage(send.error)}</Alert>}
          <Field label={how === "email" ? "To" : "Their cell"} type={how === "email" ? "email" : "tel"} value={v.to} onChange={(e) => setTo(e.target.value)} autoComplete="off" />
          {how === "email" && <Field label="Subject" value={v.subject} onChange={(e) => setSubject(e.target.value)} />}
          <TextAreaField label="Message" rows={how === "email" ? 8 : 4} value={v.message} onChange={(e) => setMessage(e.target.value)} />
          <p className="text-xs text-muted">
            {how === "email" ? "Added below your message: an Upload your new certificate button with their link." : "Their upload link is added at the end:"} <span className="break-all">{d.link}</span>
          </p>
        </div>
      )}
    </Dialog>
  );
}
